/**
 * "Main" login of a vendor account.
 *
 * PayUp emails digital checks and vendor notices to the vendor account's own
 * email (Account.email). The login whose email equals that address is the
 * main ("root") login — the same rule PayUp uses to refuse removing it.
 * The account email comes from GET /account/:id, never from the users list.
 */

export const MAIN_TAG_HELP = 'Digital checks and PayUp emails for this vendor go to this address.';
export const MAIN_EMAIL_LOCKED_HINT = 'Use Make main or Vendor Profile to change where checks go.';

const normalise = (email?: string | null) => (email ?? '').trim().toLowerCase();

export const isMainLogin = (userEmail?: string | null, accountEmail?: string | null): boolean => {
  const user = normalise(userEmail);
  return user !== '' && user === normalise(accountEmail);
};

export const vendorUserName = (row: any): string => {
  const u = row?.user ?? {};
  const full = [u.firstName ?? u.first_name, u.lastName ?? u.last_name]
    .filter((part) => !!part)
    .join(' ');
  return u.name || full || u.email || 'this user';
};

export const makeMainConfirmTitle = (row: any) => `Make ${vendorUserName(row)} the main login?`;

export const makeMainConfirmBody = (row: any) =>
  `Digital checks and PayUp emails will go to ${
    row?.user?.email ?? 'this address'
  } from now on. Checks that were already sent still go to the old address; void and reissue those.`;

export interface VendorCustomer {
  id: string;
  name: string | null;
}

export interface VendorCustomersResponse {
  customers: VendorCustomer[];
}

export const OTHER_COMPANIES_UNKNOWN =
  'We could not check whether other companies pay this vendor.';
export const OTHER_COMPANIES_CHECKING = 'Checking whether other companies pay this vendor…';

const joinNames = (names: string[]) =>
  names.length <= 1
    ? names.join('')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/**
 * Confirm-pop-up line about the OTHER companies (PayUp customers) that pay
 * this vendor, from GET /account/:id/customers. Make main changes where
 * checks go for all of them.
 * - customers unknown (call failed, or no current company to exclude) -> "could not check"
 * - no other company -> '' (add nothing)
 * - others -> "This vendor is also paid by A, B and C. ..."
 */
export const otherCompaniesNote = (
  customers: VendorCustomer[] | null | undefined,
  currentCustomerId: string | null | undefined
): string => {
  if (!customers || !currentCustomerId) return OTHER_COMPANIES_UNKNOWN;
  const names = Array.from(
    new Set(
      customers
        .filter((c) => c.id !== currentCustomerId)
        .map((c) => (c.name ?? '').trim() || 'an unnamed company')
    )
  ).sort((a, b) => a.localeCompare(b));
  if (names.length === 0) return '';
  return `This vendor is also paid by ${joinNames(
    names
  )}. The new main email applies to their checks too.`;
};

export const vendorCustomersRequest = (accountId: string) => ({
  url: `/account/${accountId}/customers`,
  method: 'GET' as const,
});

/**
 * Make main is offered only on a row that is not already main, whose login is
 * active and has an email (PayUp refuses the others).
 */
export const canOfferMakeMain = (row: any, accountEmail?: string | null): boolean => {
  const email = (row?.user?.email ?? '').trim();
  const status = String(row?.user?.status ?? '').toLowerCase();
  return email !== '' && status === 'active' && !isMainLogin(email, accountEmail);
};

export const MAKE_MAIN_FAILED =
  'Could not change the main login. Nothing was changed; try again or contact support.';
export const MAKE_MAIN_MAYBE_SAVED =
  'This may have saved. Reload the page and check the main login before trying again.';

/** PayUp error codes of POST /account/:id/main-user whose message is safe to show. */
const KNOWN_MAKE_MAIN_CODES = new Set([
  'vendor_not_found',
  'vendor_user_not_found',
  'login_not_active',
  'login_not_ready',
  'email_in_use',
  'forbidden',
]);

/** No answer from PayUp: the change may or may not have been saved. */
const MAYBE_SAVED_STATUSES = new Set<string | number>(['FETCH_ERROR', 'TIMEOUT_ERROR', 502, 504]);

export const makeMainErrorText = (error: any): string => {
  const status = error?.originalStatus ?? error?.status;
  if (MAYBE_SAVED_STATUSES.has(status)) return MAKE_MAIN_MAYBE_SAVED;
  const code = error?.data?.code;
  const message = error?.data?.message;
  if (KNOWN_MAKE_MAIN_CODES.has(code) && typeof message === 'string' && message.trim()) {
    return message;
  }
  return MAKE_MAIN_FAILED;
};

/** Request for POST /account/:id/main-user (PayUp super-admin only). */
export const makeMainUserRequest = ({
  accountId,
  userId,
}: {
  accountId: string;
  userId: string;
}) => ({
  url: `/account/${accountId}/main-user`,
  method: 'POST' as const,
  body: { user_id: userId },
});

export interface MakeMainUserResponse {
  account_id: string;
  changed: boolean;
  main_user_id: string;
  main_email: string;
  previous_main_email: string | null;
  stripe_updated: boolean;
}

interface MainLoginCellProps {
  isMain: boolean;
  canMakeMain: boolean;
  busy?: boolean;
  onMakeMain: () => void;
}

/** "Main" tag on the main row; "Make main" on other rows for super-admin. */
export const MainLoginCell = ({ isMain, canMakeMain, busy, onMakeMain }: MainLoginCellProps) => {
  if (isMain) {
    return (
      <span
        className="inline-flex px-2 py-0.5 ml-2 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800"
        title={MAIN_TAG_HELP}
        data-testid="main-login-tag"
      >
        Main
      </span>
    );
  }
  if (!canMakeMain) return null;
  return (
    <button
      type="button"
      onClick={onMakeMain}
      disabled={busy}
      className="text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
      title="Send this vendor's checks and PayUp emails to this login's email"
    >
      Make main
    </button>
  );
};
